import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { resolveDataScope, TRAINING_DATA_SCOPE } from "lib/dataScope";

export const UPDATE_FILED = "authUpdateField";
export const AUTH_SUCCESS = "authSuccess";
export const AUTH_REST = "authReset";
export const TOGGLE_DARK_MODE = "authToggleDarkMode";
export const TOGGLE_SIDE_BAR = "authToggleSideBar";
export const SET_DASHBOARD_DATA = "authDashboardData";

export const updateFields = ({ prop, value }) => ({
  type: UPDATE_FILED,
  payload: { prop, value },
});
export function setToken(token) {
  if (typeof window !== "undefined") {
    localStorage.setItem("token", token);
  }
}

export function getToken() {
  if (typeof window !== "undefined") {
    return localStorage.getItem("token");
  }
  return null;
}

export function removeToken() {
  if (typeof window !== "undefined") {
    return localStorage.removeItem("token");
  }
  return null;
}

export function getSideBar() {
  if (typeof window !== "undefined") return localStorage.getItem("sidebar");
}

export const useAuth = () => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  return {
    ...auth,
    getMe,
    getToken,
    submitLogin,
    dispatch,
    logout,
    toggleDarkMode,
    toggleSideBar,
    getSideBar,
    handleSuccessMessages,
    getDashboardData,
  };
};
const DEFAULT_API_BASE_URL = "https://vfilms.ir/api/v1";

const resolveApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ) {
    // During local development we rely on CRA proxy to avoid CORS issues.
    return "/api/v1";
  }

  return DEFAULT_API_BASE_URL;
};

export const axiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    "Accept-Language": "fa",
  },
});

// Tokens can change after the Axios instance is created. Reading it for every
// request prevents the first request after login from using a stale header.
axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }

  if (
    typeof window !== "undefined" &&
    config.url?.startsWith("/panel/") &&
    !/(?:[?&])data_scope=/.test(config.url) &&
    !config.params?.data_scope &&
    resolveDataScope(window.location.search) === TRAINING_DATA_SCOPE
  ) {
    config.params = { ...(config.params || {}), data_scope: TRAINING_DATA_SCOPE };
  }

  return config;
});
export const toggleDarkMode = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: TOGGLE_DARK_MODE,
    });
  } catch (e) {
    handleErrors(e);
  }
};

export const toggleSideBar = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: TOGGLE_SIDE_BAR,
    });
  } catch (e) {
    handleErrors(e);
  }
};

export const submitLogin = (data, cb) => async (dispatch) => {
  try {
    const result = await axiosInstance.post("/auth/login", data);
    dispatch({
      type: AUTH_SUCCESS,
      payload: { userMeta: result.data.data.me },
    });
    setToken(result.data.data.token);
    cb();
  } catch (e) {
    handleErrors(e);
  }
};
export const getMe = () => async (dispatch) => {
  try {
    const result = await axiosInstance.get("/auth/me");
    dispatch({
      type: AUTH_SUCCESS,
      payload: { userMeta: result.data.data },
    });
  } catch (e) {
    handleErrors(e);
  }
};
export const getDashboardData = (from = null, to = null) => async (
  dispatch
) => {
  dispatch(updateFields({ prop: "LoadDashboardData", value: true }));
  try {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const query = params.toString();
    const url = `/panel/core/dashboard${query ? `?${query}` : ""}`;
    const result = await axiosInstance.get(url);
    dispatch({
      type: SET_DASHBOARD_DATA,
      payload: result.data.data,
    });
  } catch (e) {
    handleErrors(e);
    dispatch({
      type: SET_DASHBOARD_DATA,
      payload: null,
    });
  } finally {
    dispatch(updateFields({ prop: "LoadDashboardData", value: false }));
  }
};
export const logout = () => async (dispatch) => {
  try {
    await axiosInstance.post("/auth/logout");
    dispatch({
      type: AUTH_REST,
    });
    removeToken();
  } catch (e) {
    dispatch({
      type: AUTH_REST,
    });
    removeToken();
  }
};

export const resetAuth = () => async (dispatch) => {
  try {
    dispatch({
      type: AUTH_REST,
    });
  } catch (e) {
    handleErrors(e);
  }
};

export const handleErrors = (e) => {
  if (e && !e.response) {
    toast.error("خطای شبکه لطفا مجددا تلاش کنید", {
      position: "bottom-left",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    return;
  }

  if (e.response.data?.data?.errors) {
    for (const value of Object.values(e.response.data.data.errors)) {
      value.forEach((element) => {
        toast.error(element, {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
    }
  }
  if (e.response && e.response.status === 401) {
    toast.error("عدم احراز هویت", {
      position: "bottom-left",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

    removeToken();
    window.location = "/";
    return;
  }
  const message = e.response?.data?.data?.message || e.response?.data?.message || "انجام عملیات با خطا مواجه شد.";
  toast.error(message, {
    position: "bottom-left",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export const handleSuccessMessages = (message) => {
  toast.success(message, {
    position: "bottom-left",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};
